import React, { useCallback } from "react";
import { Path, UseFormRegister } from "react-hook-form";

import { FormSettingColumn } from "components/FormSettingColumn";
import TextButton from "components/TextButton";
import { STRING_BOOLEAN, StringBoolean } from "constants/primitive";
import { CompanyFormConfigInputs } from "lib/types/form.setting.d";

const { TRUE, FALSE } = STRING_BOOLEAN;

export const formSettingDefaultValues: CompanyFormConfigInputs = {
  applicant: {
    name: TRUE,
    furigana: FALSE,
    address: TRUE,
    businessContent: TRUE,
    hp: FALSE,
    importantDate: TRUE,
    revenue: TRUE,
    numberOfEmployees: TRUE,
    phoneNumber: TRUE,
    email: TRUE,
    fax: FALSE,
    contactPerson: {
      name: TRUE,
      department: TRUE,
      phoneNumber: TRUE,
      email: TRUE,
      fax: FALSE,
    },
    historyDoc: TRUE,
    idCard: TRUE,
    insuranceCard: TRUE,
  },
  application: {
    useStartDate: TRUE,
    intendedUse: TRUE,
    hopeSpaces: TRUE,
  },
  car: {
    sizeOfCar: TRUE,
    carModel: TRUE,
    numberPlate: TRUE,
    colorOfCar: TRUE,
  },
  user: {
    name: TRUE,
    address: TRUE,
    relationship: FALSE,
    birthday: FALSE,
    phoneNumber: TRUE,
    email: TRUE,
    employmentCompanyName: FALSE,
    employmentCompanyAddress: FALSE,
    idCard: TRUE,
    certificate: TRUE,
  },
  guarantor: {
    name: TRUE,
    address: TRUE,
    familyRelationship: TRUE,
    birthday: TRUE,
    revenue: TRUE,
    phoneNumber: TRUE,
    email: TRUE,
    fax: FALSE,
    employmentCompanyName: FALSE,
    employmentCompanyAddress: FALSE,
    lengthOfEmployment: FALSE,
    hp: FALSE,
    importantDate: FALSE,
    capital: FALSE,
    annualRevenue: FALSE,
    numberOfEmployees: FALSE,
    cosignerIdCard: TRUE,
  },
};

// setValueをPathで指定したフィールドに対して一括で行う
const setValueTool = (
  setValue: (fieldName: Path<CompanyFormConfigInputs>, value?: StringBoolean) => void,
  field: Path<CompanyFormConfigInputs>[],
  value?: StringBoolean
) => {
  field.forEach((f) => {
    setValue(f, value);
  });
};

export const ApplicantBlock = ({
  register,
  watch,
  setValue,
  data,
}: {
  register: UseFormRegister<CompanyFormConfigInputs>;
  watch: (fieldName: Path<CompanyFormConfigInputs>) => StringBoolean;
  setValue: (fieldName: Path<CompanyFormConfigInputs>, value?: StringBoolean) => void;
  data: CompanyFormConfigInputs["applicant"];
}) => {
  const fields: Path<CompanyFormConfigInputs>[] = [
    "applicant.name",
    "applicant.furigana",
    "applicant.address",
    "applicant.businessContent",
    "applicant.hp",
    "applicant.importantDate",
    "applicant.revenue",
    "applicant.numberOfEmployees",
    "applicant.phoneNumber",
    "applicant.email",
    "applicant.fax",
    "applicant.idCard",
    "applicant.historyDoc",
    "applicant.insuranceCard",
  ];

  const resetToDefault = useCallback(() => {
    setValue("applicant.name", formSettingDefaultValues.applicant.name);
    setValue("applicant.furigana", formSettingDefaultValues.applicant.furigana);
    setValue("applicant.address", formSettingDefaultValues.applicant.address);
    setValue("applicant.businessContent", formSettingDefaultValues.applicant.businessContent);
    setValue("applicant.hp", formSettingDefaultValues.applicant.hp);
    setValue("applicant.importantDate", formSettingDefaultValues.applicant.importantDate);
    setValue("applicant.revenue", formSettingDefaultValues.applicant.revenue);
    setValue("applicant.numberOfEmployees", formSettingDefaultValues.applicant.numberOfEmployees);
    setValue("applicant.phoneNumber", formSettingDefaultValues.applicant.phoneNumber);
    setValue("applicant.email", formSettingDefaultValues.applicant.email);
    setValue("applicant.fax", formSettingDefaultValues.applicant.fax);
    setValue("applicant.idCard", formSettingDefaultValues.applicant.idCard);
    setValue("applicant.historyDoc", formSettingDefaultValues.applicant.historyDoc);
    setValue("applicant.insuranceCard", formSettingDefaultValues.applicant.insuranceCard);
  }, [setValue]);

  return (
    <div className="border px-[40px] py-4">
      <div className="flex h-[61px] justify-between pt-1 ">
        <span className="text-[19.2px] font-bold">契約者</span>
        <div className="flex gap-6 self-start">
          <TextButton
            text={"すべて必須に変更"}
            style="text-s"
            onClick={() => {
              setValueTool(setValue, fields, TRUE);
            }}
          />
          <TextButton
            text={"すべて任意に変更"}
            style="text-s"
            onClick={() => {
              setValueTool(setValue, fields, FALSE);
            }}
          />
          <TextButton text={"初期設定に戻す"} style="text-s" onClick={resetToDefault} />
        </div>
      </div>
      <div>
        <div className="flex h-[44px] items-center bg-gray-light px-6 text-s">
          <span className="min-w-[230px]">法人契約希望</span>
          <span className="min-w-[230px]">個人契約希望</span>
          <span className="min-w-[230px]">必須 / 任意</span>
        </div>
        <FormSettingColumn
          field="applicant.name"
          legalName="契約者名"
          personName="契約者名"
          register={register}
          watch={watch}
          defaultValue={data.name}
          noField
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="applicant.furigana"
          legalName="フリガナ"
          personName="フリガナ"
          defaultValue={data.furigana}
          register={register}
          watch={watch}
          minWidthRadio="min-w-0"
        />

        <FormSettingColumn
          field="applicant.address"
          legalName="会社所在地"
          personName="住所"
          defaultValue={data.address}
          register={register}
          watch={watch}
          noField
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="applicant.businessContent"
          legalName="事業内容"
          personName="-"
          defaultValue={data.businessContent}
          register={register}
          watch={watch}
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="applicant.hp"
          legalName="会社HP"
          personName="-"
          defaultValue={data.hp}
          register={register}
          watch={watch}
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="applicant.importantDate"
          legalName="設立年月日"
          personName="生年月日"
          defaultValue={data.importantDate}
          register={register}
          watch={watch}
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="applicant.revenue"
          legalName="資本金"
          personName="年収（税込）"
          defaultValue={data.revenue}
          register={register}
          watch={watch}
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="applicant.numberOfEmployees"
          legalName="従業員数"
          personName="-"
          defaultValue={data.numberOfEmployees}
          register={register}
          watch={watch}
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="applicant.phoneNumber"
          legalName="電話番号"
          personName="電話番号"
          defaultValue={data.phoneNumber}
          register={register}
          watch={watch}
          noField
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="applicant.email"
          legalName="メールアドレス"
          personName="メールアドレス"
          defaultValue={data.email}
          register={register}
          watch={watch}
          noField
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="applicant.fax"
          legalName="FAX"
          personName="FAX"
          isLast={true}
          defaultValue={data.fax}
          register={register}
          watch={watch}
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="applicant.idCard"
          legalName="代表者身分証"
          personName="身分証"
          defaultValue={data.idCard}
          register={register}
          watch={watch}
          noField
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="applicant.historyDoc"
          legalName="履歴事項全部証明書"
          personName="-"
          defaultValue={data.historyDoc}
          register={register}
          watch={watch}
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="applicant.insuranceCard"
          legalName="-"
          personName="任意保険証"
          defaultValue={data.insuranceCard}
          register={register}
          watch={watch}
          isLast
          minWidthRadio="min-w-0"
        />
      </div>
    </div>
  );
};

export const ContactPersonBlock = ({
  register,
  watch,
  setValue,
  data,
}: {
  register: UseFormRegister<CompanyFormConfigInputs>;
  watch: (fieldName: Path<CompanyFormConfigInputs>) => StringBoolean;
  setValue: (fieldName: Path<CompanyFormConfigInputs>, value?: StringBoolean) => void;
  data: CompanyFormConfigInputs["applicant"]["contactPerson"];
}) => {
  const fields: Path<CompanyFormConfigInputs>[] = [
    "applicant.contactPerson.name",
    "applicant.contactPerson.department",
    "applicant.contactPerson.phoneNumber",
    "applicant.contactPerson.email",
    "applicant.contactPerson.fax",
  ];

  const resetToDefault = useCallback(() => {
    setValue("applicant.contactPerson.name", formSettingDefaultValues.applicant.contactPerson.name);
    setValue("applicant.contactPerson.department", formSettingDefaultValues.applicant.contactPerson.department);
    setValue("applicant.contactPerson.phoneNumber", formSettingDefaultValues.applicant.contactPerson.phoneNumber);
    setValue("applicant.contactPerson.email", formSettingDefaultValues.applicant.contactPerson.email);
    setValue("applicant.contactPerson.fax", formSettingDefaultValues.applicant.contactPerson.fax);
  }, [setValue]);

  return (
    <div className="border px-[40px] py-4">
      <div className="flex h-[61px] justify-between pt-1 ">
        <span className="text-[19.2px] font-bold">担当者</span>
        <div className="flex gap-6 self-start">
          <TextButton
            text={"すべて必須に変更"}
            style="text-s"
            onClick={() => {
              setValueTool(setValue, fields, TRUE);
            }}
          />
          <TextButton
            text={"すべて任意に変更"}
            style="text-s"
            onClick={() => {
              setValueTool(setValue, fields, FALSE);
            }}
          />
          <TextButton text={"初期設定に戻す"} style="text-s" onClick={resetToDefault} />
        </div>
      </div>
      <div>
        <div className="flex h-[44px] items-center bg-gray-light px-6 text-s">
          <span className="min-w-[230px]">法人契約希望</span>
          <span className="min-w-[230px]">個人契約希望</span>
          <span className="min-w-[230px]">必須 / 任意</span>
        </div>
        <FormSettingColumn
          field="applicant.contactPerson.name"
          legalName="氏名"
          personName="-"
          defaultValue={data.name}
          register={register}
          watch={watch}
          noField
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="applicant.contactPerson.department"
          legalName="所属部署"
          personName="-"
          defaultValue={data.department}
          register={register}
          watch={watch}
          noField
          minWidthRadio="min-w-0"
        />

        <FormSettingColumn
          field="applicant.contactPerson.phoneNumber"
          legalName="電話番号"
          personName="-"
          defaultValue={data.phoneNumber}
          register={register}
          watch={watch}
          noField
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="applicant.contactPerson.email"
          legalName="メールアドレス"
          personName="-"
          defaultValue={data.email}
          register={register}
          watch={watch}
          noField
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="applicant.contactPerson.fax"
          legalName="FAX"
          personName="-"
          isLast={true}
          defaultValue={data.fax}
          register={register}
          watch={watch}
          minWidthRadio="min-w-0"
        />
      </div>
    </div>
  );
};

export const ApplicationBlock = ({
  register,
  watch,
  setValue,
  data,
}: {
  register: UseFormRegister<CompanyFormConfigInputs>;
  watch: (fieldName: Path<CompanyFormConfigInputs>) => StringBoolean;
  setValue: (fieldName: Path<CompanyFormConfigInputs>, value?: StringBoolean) => void;
  data: CompanyFormConfigInputs["application"];
}) => {
  const fields: Path<CompanyFormConfigInputs>[] = [
    "application.useStartDate",
    "application.hopeSpaces",
    "application.intendedUse",
  ];

  const resetToDefault = useCallback(() => {
    setValue("application.useStartDate", formSettingDefaultValues.application.useStartDate);
    setValue("application.intendedUse", formSettingDefaultValues.application.intendedUse);
    setValue("application.hopeSpaces", formSettingDefaultValues.application.hopeSpaces);
    setValue("applicant.numberOfEmployees", formSettingDefaultValues.applicant.numberOfEmployees);
  }, [setValue]);

  return (
    <div className="border px-[40px] py-4">
      <div className="flex h-[61px] justify-between pt-1 ">
        <span className="text-[19.2px] font-bold">契約内容</span>
        <div className="flex gap-6 self-start">
          <TextButton
            text={"すべて必須に変更"}
            style="text-s"
            onClick={() => {
              setValueTool(setValue, fields, TRUE);
            }}
          />
          <TextButton
            text={"すべて任意に変更"}
            style="text-s"
            onClick={() => {
              setValueTool(setValue, fields, FALSE);
            }}
          />
          <TextButton text={"初期設定に戻す"} style="text-s" onClick={resetToDefault} />
        </div>
      </div>
      <div>
        <div className="flex h-[44px] items-center bg-gray-light px-6 text-s">
          <span className="min-w-[230px]">法人契約希望</span>
          <span className="min-w-[230px]">個人契約希望</span>
          <span className="min-w-[230px]">必須 / 任意</span>
        </div>
        <FormSettingColumn
          field="application.useStartDate"
          legalName="利用開始希望日"
          personName="利用開始希望日"
          defaultValue={TRUE}
          register={register}
          watch={watch}
          noField
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="application.hopeSpaces"
          legalName="利用区画"
          personName="利用区画"
          register={register}
          watch={watch}
          noField
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="application.intendedUse"
          legalName="使用用途"
          personName="使用用途"
          defaultValue={data.intendedUse}
          register={register}
          watch={watch}
          minWidthRadio="min-w-0"
        />
        <div className="mt-4 text-xs text-gray">※希望区分が表示されるのは、空き状況を公開している区画のみ</div>
      </div>
    </div>
  );
};

export const CarBlock = ({
  register,
  watch,
  setValue,
  data,
}: {
  register: UseFormRegister<CompanyFormConfigInputs>;
  watch: (fieldName: Path<CompanyFormConfigInputs>) => StringBoolean;
  setValue: (fieldName: Path<CompanyFormConfigInputs>, value?: StringBoolean) => void;
  data: CompanyFormConfigInputs["car"];
}) => {
  const fields: Path<CompanyFormConfigInputs>[] = [
    "car.sizeOfCar",
    "car.carModel",
    "car.numberPlate",
    "car.colorOfCar",
  ];

  const resetToDefault = useCallback(() => {
    setValue("car.sizeOfCar", formSettingDefaultValues.car.sizeOfCar);
    setValue("car.carModel", formSettingDefaultValues.car.carModel);
    setValue("car.numberPlate", formSettingDefaultValues.car.numberPlate);
    setValue("car.colorOfCar", formSettingDefaultValues.car.colorOfCar);
  }, [setValue]);

  return (
    <div className="border px-[40px] py-4">
      <div className="flex h-[61px] justify-between pt-1 ">
        <span className="text-[19.2px] font-bold">利用車種</span>
        <div className="flex gap-6 self-start">
          <TextButton
            text={"すべて必須に変更"}
            style="text-s"
            onClick={() => {
              setValueTool(setValue, fields, TRUE);
            }}
          />
          <TextButton
            text={"すべて任意に変更"}
            style="text-s"
            onClick={() => {
              setValueTool(setValue, fields, FALSE);
            }}
          />
          <TextButton text={"初期設定に戻す"} style="text-s" onClick={resetToDefault} />
        </div>
      </div>
      <div>
        <div className="flex h-[44px] items-center bg-gray-light px-6 text-s">
          <span className="min-w-[230px]">法人契約希望</span>
          <span className="min-w-[230px]">個人契約希望</span>
          <span className="min-w-[230px]">必須 / 任意</span>
        </div>
        <FormSettingColumn
          field="car.sizeOfCar"
          legalName="車両サイズ"
          personName="車両サイズ"
          defaultValue={data.sizeOfCar}
          register={register}
          watch={watch}
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="car.carModel"
          legalName="車種"
          personName="車種"
          defaultValue={data.carModel}
          register={register}
          watch={watch}
          minWidthRadio="min-w-0"
        />

        <FormSettingColumn
          field="car.numberPlate"
          legalName="ナンバー"
          personName="ナンバー"
          defaultValue={data.numberPlate}
          register={register}
          watch={watch}
          noField
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="car.colorOfCar"
          legalName="車体色"
          personName="車体色"
          defaultValue={data.colorOfCar}
          register={register}
          watch={watch}
          minWidthRadio="min-w-0"
        />
        <div className="mt-4 text-xs text-gray">※申込の際に、希望台数分の利用車種の入力が必要になります</div>
      </div>
    </div>
  );
};

export const UserBlock = ({
  register,
  watch,
  setValue,
  data,
}: {
  register: UseFormRegister<CompanyFormConfigInputs>;
  watch: (fieldName: Path<CompanyFormConfigInputs>) => StringBoolean;
  setValue: (fieldName: Path<CompanyFormConfigInputs>, value?: StringBoolean) => void;
  data: CompanyFormConfigInputs["user"];
}) => {
  const fields: Path<CompanyFormConfigInputs>[] = [
    "user.name",
    "user.address",
    "user.relationship",
    "user.birthday",
    "user.phoneNumber",
    "user.email",
    "user.employmentCompanyName",
    "user.employmentCompanyAddress",
    "user.idCard",
    "user.certificate",
  ];

  const resetToDefault = useCallback(() => {
    setValue("user.name", formSettingDefaultValues.user.name);
    setValue("user.address", formSettingDefaultValues.user.address);
    setValue("user.relationship", formSettingDefaultValues.user.relationship);
    setValue("user.birthday", formSettingDefaultValues.user.birthday);
    setValue("user.phoneNumber", formSettingDefaultValues.user.phoneNumber);
    setValue("user.email", formSettingDefaultValues.user.email);
    setValue("user.employmentCompanyName", formSettingDefaultValues.user.employmentCompanyName);
    setValue("user.employmentCompanyAddress", formSettingDefaultValues.user.employmentCompanyAddress);
    setValue("user.idCard", formSettingDefaultValues.user.idCard);
    setValue("user.certificate", formSettingDefaultValues.user.certificate);
  }, [setValue]);

  return (
    <div className="border px-[40px] py-4">
      <div className="flex h-[61px] justify-between pt-1 ">
        <span className="text-[19.2px] font-bold">利用者</span>
        <div className="flex gap-6 self-start">
          <TextButton
            text={"すべて必須に変更"}
            style="text-s"
            onClick={() => {
              setValueTool(setValue, fields, TRUE);
            }}
          />
          <TextButton
            text={"すべて任意に変更"}
            style="text-s"
            onClick={() => {
              setValueTool(setValue, fields, FALSE);
            }}
          />
          <TextButton text={"初期設定に戻す"} style="text-s" onClick={resetToDefault} />
        </div>
      </div>
      <div>
        <div className="flex h-[44px] items-center bg-gray-light px-6 text-s">
          <span className="min-w-[230px]">法人契約希望</span>
          <span className="min-w-[230px]">個人契約希望</span>
          <span className="min-w-[230px]">必須 / 任意</span>
        </div>
        <FormSettingColumn
          field="user.name"
          legalName="氏名"
          personName="氏名"
          defaultValue={data.name}
          register={register}
          watch={watch}
          noField
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="user.address"
          legalName="住所"
          personName="住所"
          defaultValue={data.address}
          register={register}
          watch={watch}
          noField
          minWidthRadio="min-w-0"
        />

        <FormSettingColumn
          field="user.relationship"
          legalName="続柄"
          personName="続柄"
          defaultValue={data.relationship}
          register={register}
          watch={watch}
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="user.birthday"
          legalName="生年月日"
          personName="生年月日"
          defaultValue={data.birthday}
          register={register}
          watch={watch}
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="user.phoneNumber"
          legalName="電話番号"
          personName="電話番号"
          defaultValue={data.phoneNumber}
          register={register}
          watch={watch}
          noField
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="user.email"
          legalName="メールアドレス"
          personName="メールアドレス"
          defaultValue={data.email}
          register={register}
          watch={watch}
          noField
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="user.employmentCompanyName"
          legalName="勤務先名称"
          personName="勤務先名称"
          defaultValue={data.employmentCompanyName}
          register={register}
          watch={watch}
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="user.employmentCompanyAddress"
          legalName="勤務先住所"
          personName="勤務先住所"
          defaultValue={data.employmentCompanyAddress}
          register={register}
          watch={watch}
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="user.idCard"
          legalName="運転免許証"
          personName="運転免許証"
          defaultValue={data.idCard}
          register={register}
          watch={watch}
          noField
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="user.certificate"
          legalName="車検証"
          personName="車検証"
          defaultValue={data.certificate}
          register={register}
          watch={watch}
          minWidthRadio="min-w-0"
        />
        <div className="mt-4 text-xs text-gray">※申込の際に、希望台数分の利用者の入力が必要になります</div>
      </div>
    </div>
  );
};

export const GuarantorBlock = ({
  register,
  watch,
  setValue,
  data,
}: {
  register: UseFormRegister<CompanyFormConfigInputs>;
  watch: (fieldName: Path<CompanyFormConfigInputs>) => StringBoolean;
  setValue: (fieldName: Path<CompanyFormConfigInputs>, value?: StringBoolean) => void;
  data: CompanyFormConfigInputs["guarantor"];
}) => {
  const fields: Path<CompanyFormConfigInputs>[] = [
    "guarantor.name",
    "guarantor.address",
    "guarantor.familyRelationship",
    "guarantor.birthday",
    "guarantor.revenue",
    "guarantor.phoneNumber",
    "guarantor.email",
    "guarantor.fax",
    "guarantor.employmentCompanyName",
    "guarantor.employmentCompanyAddress",
    "guarantor.lengthOfEmployment",
    "guarantor.hp",
    "guarantor.importantDate",
    "guarantor.capital",
    "guarantor.annualRevenue",
    "guarantor.numberOfEmployees",
    "guarantor.cosignerIdCard",
  ];

  const resetToDefault = useCallback(() => {
    setValue("guarantor.name", formSettingDefaultValues.guarantor.name);
    setValue("guarantor.address", formSettingDefaultValues.guarantor.address);
    setValue("guarantor.familyRelationship", formSettingDefaultValues.guarantor.familyRelationship);
    setValue("guarantor.birthday", formSettingDefaultValues.guarantor.birthday);
    setValue("guarantor.revenue", formSettingDefaultValues.guarantor.revenue);
    setValue("guarantor.phoneNumber", formSettingDefaultValues.guarantor.phoneNumber);
    setValue("guarantor.email", formSettingDefaultValues.guarantor.email);
    setValue("guarantor.fax", formSettingDefaultValues.guarantor.fax);
    setValue("guarantor.employmentCompanyName", formSettingDefaultValues.guarantor.employmentCompanyName);
    setValue("guarantor.employmentCompanyAddress", formSettingDefaultValues.guarantor.employmentCompanyAddress);
    setValue("guarantor.lengthOfEmployment", formSettingDefaultValues.guarantor.lengthOfEmployment);
    setValue("guarantor.hp", formSettingDefaultValues.guarantor.hp);
    setValue("guarantor.importantDate", formSettingDefaultValues.guarantor.importantDate);
    setValue("guarantor.capital", formSettingDefaultValues.guarantor.capital);
    setValue("guarantor.annualRevenue", formSettingDefaultValues.guarantor.annualRevenue);
    setValue("guarantor.numberOfEmployees", formSettingDefaultValues.guarantor.numberOfEmployees);
    setValue("guarantor.cosignerIdCard", formSettingDefaultValues.guarantor.cosignerIdCard);
  }, [setValue]);

  return (
    <div className="border px-[40px] py-4">
      <div className="flex h-[61px] justify-between pt-1 ">
        <span className="text-[19.2px] font-bold">緊急連絡先・連帯保証人</span>
        <div className="flex gap-6 self-start">
          <TextButton
            text={"すべて必須に変更"}
            style="text-s"
            onClick={() => {
              setValueTool(setValue, fields, TRUE);
            }}
          />
          <TextButton
            text={"すべて任意に変更"}
            style="text-s"
            onClick={() => {
              setValueTool(setValue, fields, FALSE);
            }}
          />
          <TextButton text={"初期設定に戻す"} style="text-s" onClick={resetToDefault} />
        </div>
      </div>
      <div>
        <div className="flex h-[44px] items-center bg-gray-light px-6">
          <span className="min-w-[230px]">連帯保証人</span>
          <span className="min-w-[230px]">緊急連絡先</span>
          <span className="min-w-[230px]">必須 / 任意</span>
        </div>
        <FormSettingColumn
          field="guarantor.name"
          legalName="氏名"
          personName="氏名"
          defaultValue={data.name}
          register={register}
          watch={watch}
          noField
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="guarantor.address"
          legalName="住所"
          personName="住所"
          defaultValue={data.address}
          register={register}
          watch={watch}
          noField
          minWidthRadio="min-w-0"
        />

        <FormSettingColumn
          field="guarantor.familyRelationship"
          legalName="続柄"
          personName="続柄"
          defaultValue={data.familyRelationship}
          register={register}
          watch={watch}
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="guarantor.birthday"
          legalName="生年月日"
          personName="生年月日"
          defaultValue={data.birthday}
          register={register}
          watch={watch}
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="guarantor.revenue"
          legalName="年収(税込)"
          personName="-"
          defaultValue={data.revenue}
          register={register}
          watch={watch}
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="guarantor.phoneNumber"
          legalName="電話番号"
          personName="電話番号"
          defaultValue={data.phoneNumber}
          register={register}
          watch={watch}
          noField
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="guarantor.email"
          legalName="メールアドレス"
          personName="メールアドレス"
          defaultValue={data.email}
          register={register}
          watch={watch}
          noField
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="guarantor.fax"
          legalName="FAX"
          personName="FAX"
          defaultValue={data.fax}
          register={register}
          watch={watch}
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="guarantor.employmentCompanyName"
          legalName="勤務先名称"
          personName="勤務先名称"
          defaultValue={data.employmentCompanyName}
          register={register}
          watch={watch}
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="guarantor.employmentCompanyAddress"
          legalName="勤務先住所"
          personName="勤務先住所"
          defaultValue={data.employmentCompanyAddress}
          register={register}
          watch={watch}
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="guarantor.lengthOfEmployment"
          legalName="勤続年数"
          personName="勤続年数"
          defaultValue={data.lengthOfEmployment}
          register={register}
          watch={watch}
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="guarantor.hp"
          legalName="会社HP"
          personName="会社HP"
          defaultValue={data.hp}
          register={register}
          watch={watch}
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="guarantor.importantDate"
          legalName="設立年月日"
          personName="設立年月日"
          defaultValue={data.importantDate}
          register={register}
          watch={watch}
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="guarantor.capital"
          legalName="資本金"
          personName="資本金"
          defaultValue={data.capital}
          register={register}
          watch={watch}
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="guarantor.annualRevenue"
          legalName="年商"
          personName="年商"
          defaultValue={data.annualRevenue}
          register={register}
          watch={watch}
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="guarantor.numberOfEmployees"
          legalName="従業員数"
          personName="従業員数"
          defaultValue={data.numberOfEmployees}
          register={register}
          watch={watch}
          minWidthRadio="min-w-0"
        />
        <FormSettingColumn
          field="guarantor.cosignerIdCard"
          legalName="身分証"
          personName="-"
          defaultValue={data.cosignerIdCard}
          register={register}
          watch={watch}
          isLast
          noField
          minWidthRadio="min-w-0"
        />
      </div>
    </div>
  );
};
